// Marketing attribution capture for the viewing booking funnel.
//
// Captures utm_*/gclid/gbraid/wbraid/fbclid + landing page + referrer
// whenever a visitor arrives with a genuine "new touch" signal (an ad/UTM
// param, or an external referrer) and persists two snapshots in
// localStorage:
//   - first touch: written once, never overwritten by later visits.
//   - last touch: overwritten by every subsequent qualifying touch.
// Plain in-site navigation (no params, no external referrer) is a no-op so
// browsing several pages before booking doesn't erase first-touch or spam
// last-touch with empty data.
//
// Everything here fails silently - a blocked/unavailable localStorage, a
// malformed URL, or any other error must never break the page or the
// booking flow that reads getStoredAttribution() later.

import { getStoredConsent } from "@/lib/consent";

const FIRST_TOUCH_KEY = "eg_attribution_first_touch";
const LAST_TOUCH_KEY = "eg_attribution_last_touch";

export interface AttributionTouch {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
  adSet?: string;
  adGroup?: string;
  placement?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  landingPage?: string;
  referrer?: string;
  capturedAt?: string;
}

/**
 * Marketing attribution is gated on the site's existing "advertising
 * cookies" consent category, matching the default-denied posture the app's
 * Google Consent Mode and cookie banner already use elsewhere (ad_storage
 * starts "denied" and the banner's advertising toggle starts off). No
 * stored decision yet - i.e. the visitor hasn't answered the banner - is
 * treated the same as denied, not allowed: only an explicit "ads: true"
 * (Accept All, or Manage > Advertising Cookies on) permits capture.
 */
function attributionAllowed(): boolean {
  try {
    // getStoredConsent() applies the same 180-day expiry ConsentBanner
    // uses, returning null for an expired record (and clearing it) - so a
    // visitor whose consent has lapsed is treated as denied again, not as
    // still-granted, until they renew it.
    const consent = getStoredConsent();
    return consent?.ads === true;
  } catch {
    return false;
  }
}

/**
 * Exported so any other place that reads/sends marketing identifiers (e.g.
 * the booking form's legacy flat `readTrackingParams()` UTM/fbclid fields)
 * can gate on the exact same advertising-consent rule as attribution
 * capture, instead of re-implementing or accidentally skipping the check.
 */
export function isAdvertisingConsentGranted(): boolean {
  return attributionAllowed();
}

// Matches the expiry window Google Ads' own click-through conversion
// window commonly uses, and keeps stored attribution from persisting
// indefinitely once a visitor has granted consent - old touches beyond
// this age are treated as if never captured and are cleared on read.
const ATTRIBUTION_MAX_AGE_DAYS = 90;

function readStored(key: string): AttributionTouch | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const touch = JSON.parse(raw) as AttributionTouch;
    if (touch.capturedAt) {
      const ageDays = (Date.now() - new Date(touch.capturedAt).getTime()) / (1000 * 60 * 60 * 24);
      if (isNaN(ageDays) || ageDays > ATTRIBUTION_MAX_AGE_DAYS) {
        localStorage.removeItem(key);
        return null;
      }
    }
    return touch;
  } catch {
    return null;
  }
}

function writeStored(key: string, touch: AttributionTouch): void {
  try {
    localStorage.setItem(key, JSON.stringify(touch));
  } catch {
    // Storage unavailable/full/blocked - silently skip persistence.
  }
}

function clearStored(): void {
  try {
    localStorage.removeItem(FIRST_TOUCH_KEY);
    localStorage.removeItem(LAST_TOUCH_KEY);
  } catch {
    // ignore
  }
}

/**
 * Wipes any stored attribution immediately. Call this the moment a visitor
 * explicitly rejects advertising cookies (see ConsentBanner's Reject/Save
 * handlers) - capture only runs on route changes, so without this an
 * already-stored touch could otherwise still ride along on a booking
 * submitted right after opting out, with no navigation in between.
 */
export function clearAttributionOnConsentWithdrawal(): void {
  clearStored();
}

function getHostname(url: string): string | null {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

/** Reads utm_ and click-id params from the current URL. Returns undefined for any param not present. */
function readUrlAttributionParams(): Omit<AttributionTouch, "landingPage" | "referrer" | "capturedAt"> {
  const params = new URLSearchParams(window.location.search);
  const pick = (key: string) => params.get(key) || undefined;
  return {
    source: pick("utm_source"),
    medium: pick("utm_medium"),
    campaign: pick("utm_campaign"),
    term: pick("utm_term"),
    content: pick("utm_content"),
    adSet: pick("utm_adset"),
    adGroup: pick("utm_adgroup"),
    placement: pick("utm_placement"),
    gclid: pick("gclid"),
    gbraid: pick("gbraid"),
    wbraid: pick("wbraid"),
    fbclid: pick("fbclid"),
  };
}

// `document.referrer` is fixed for the lifetime of the document - it does
// NOT change as a single-page app navigates between routes. So it is only
// a valid "how did this visitor arrive" signal the first time we check it
// after a real page load; treating it as a signal on every SPA route
// change would re-detect the same stale external referrer on every
// internal navigation and stomp last-touch with an empty/wrong touch.
// This flag makes the referrer check fire (at most) once per page load.
let referrerCheckedThisPageLoad = false;

/**
 * Call on every page load (and SPA route change) to capture attribution if
 * this looks like a genuine new touch. Safe to call unconditionally - it's
 * a no-op for plain internal navigation with no tracking signal.
 */
export function captureAttribution(): void {
  try {
    if (typeof window === "undefined") return;
    if (!attributionAllowed()) {
      clearStored();
      return;
    }

    const urlParams = readUrlAttributionParams();
    const hasUrlParam = Object.values(urlParams).some(Boolean);

    let isExternalReferrer = false;
    let referrer = "";
    if (!referrerCheckedThisPageLoad) {
      referrerCheckedThisPageLoad = true;
      referrer = document.referrer || "";
      const referrerHost = referrer ? getHostname(referrer) : null;
      isExternalReferrer = !!referrerHost && referrerHost !== window.location.hostname;
    }

    // No genuine new-touch signal (e.g. plain in-site navigation, or a
    // later route change with no fresh UTM/click-id params) - leave
    // whatever first/last touch is already stored untouched.
    if (!hasUrlParam && !isExternalReferrer) return;

    const touch: AttributionTouch = {
      ...urlParams,
      landingPage: window.location.pathname + window.location.search,
      referrer: isExternalReferrer ? referrer : undefined,
      capturedAt: new Date().toISOString(),
    };

    if (!readStored(FIRST_TOUCH_KEY)) {
      writeStored(FIRST_TOUCH_KEY, touch);
    }
    writeStored(LAST_TOUCH_KEY, touch);
  } catch {
    // Attribution must never break the page.
  }
}

/**
 * Reads whatever first/last-touch attribution is currently stored, for
 * attaching to a booking submission. Re-checks consent here (not just at
 * capture time) so a visitor who rejects advertising cookies after a touch
 * was captured - but before/without triggering another capture - never has
 * that stored data attached to a booking they submit afterwards.
 */
export function getStoredAttribution(): { firstTouch: AttributionTouch | null; lastTouch: AttributionTouch | null } {
  try {
    if (!attributionAllowed()) {
      clearStored();
      return { firstTouch: null, lastTouch: null };
    }
    return { firstTouch: readStored(FIRST_TOUCH_KEY), lastTouch: readStored(LAST_TOUCH_KEY) };
  } catch {
    return { firstTouch: null, lastTouch: null };
  }
}
